<template>
  <div>
    <button
      type="button"
      class="btn btn-sm btn-outline-secondary"
      :title="title"
      :disabled="uploading"
      @click="openSelector"
    >
      <i v-if="uploading" class="fas fa-spinner fa-spin fa-fw"></i>
      <i v-else :class="['fas', icon, 'fa-fw']"></i>
    </button>

    <input
      ref="fileInputRef"
      type="file"
      class="d-none"
      :accept="accept"
      @change="handleUpload"
    />

    <div v-if="errorMessage" class="text-danger small mt-1">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
/**
 * BadiuTiptapFileUpload
 *
 * Generic upload component responsible for:
 *  - Opening a file selector dialog
 *  - Sending the selected file to the Badiu API
 *  - Emitting events with the public URL and file metadata
 *
 * Reused by:
 *  - BadiuTiptapImageUpload (images)
 *  - BadiuTiptapLink        (file attachments for links)
 *
 * URL structure (built from baseUrl + constants below):
 *  - Upload endpoint : {baseUrl}/system/file/set/single
 *  - Public read URL : {baseUrl}/system/file/get/{code}/{name}
 */
import { ref, computed } from 'vue'

// Path segments are fixed by the Badiu API contract and never change.
const FILE_SUBMIT_PATH = "/system/file/set/single";
const FILE_READ_PATH   = "/system/file/get/";

export default {
  name: "BadiuTiptapFileUpload",

  props: {
    accept: {
      type: String,
      default: "*/*"
    },

    filetype: {
      type: String,
      default: "file"
    },

    title: {
      type: String,
      default: "Upload file"
    },

    /** Font Awesome icon class (e.g. "fa-upload", "fa-paperclip"). */
    icon: {
      type: String,
      default: "fa-upload"
    },

    /** Objeto de configuração (ver BadiuTiptapEditor para doc das chaves). */
    config: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ["uploaded", "error"],

  setup(props, { emit }) {
    const fileInputRef   = ref(null);
    const uploading      = ref(false);
    const errorMessage   = ref("");

    const DEFAULT_BASE_URL = "https://ds4.badiu21.com.br/badiunet/geral/public";

    const baseUrl = computed(() =>
      props.config?.upload_baseurl ?? DEFAULT_BASE_URL
    );

    /** axios vem de config.external_api_vendor.axios — sem fallback para window. */
    const axios = computed(() =>
      props.config?.external_api_vendor?.axios ?? null
    );

    /** Full endpoint used to submit files. */
    const uploadUrl = computed(() => baseUrl.value + FILE_SUBMIT_PATH);

    /** Base URL prefix used to build the public read URL after upload. */
    const publicReadUrl = computed(() => baseUrl.value + FILE_READ_PATH);

    function openSelector() {
      errorMessage.value = "";
      fileInputRef.value?.click();
    }

    async function handleUpload(event) {
      const file = event.target.files?.[0];
      event.target.value = "";

      if (!file) return;

      uploading.value    = true;
      errorMessage.value = "";

      try {
        if (!axios.value) {
          throw new Error("axios não disponível. Forneça external_api_vendor no config.");
        }

        const fd = new FormData();
        fd.append("file", file);
        fd.append("filetype", props.filetype);

        const response = await axios.value.post(uploadUrl.value, fd);

        if (
          response.data &&
          response.data.status === "accept" &&
          response.data.message
        ) {
          const fileCode  = response.data.message.code;
          const fileName  = response.data.message.name;
          const filepath  = fileCode + "/" + fileName;
          const publicUrl = publicReadUrl.value + filepath;

          emit("uploaded", {
            file,
            response: response.data,
            filepath,
            src: publicUrl,
            url: publicUrl,
            name: file.name
          });
        } else {
          throw new Error(
            (response.data && response.data.message) ||
              "Unexpected server response."
          );
        }
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          error.message ||
          "Failed to upload file.";

        errorMessage.value = msg;

        emit("error", { file, error, message: msg });
      } finally {
        uploading.value = false;
      }
    }

    return {
      fileInputRef,
      uploading,
      errorMessage,
      openSelector,
      handleUpload
    };
  }
};
</script>
